/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 2 hook(s) need dependency review; fix each and remove this directive */
import { contractEntitiesUrl } from "@Services/dataManager";
import { forwardRef, useEffect, useRef, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import IconComponent from "@CubeComponents/icons";
import { GrClose } from "react-icons/gr";
import axiosWrapper from "@Services/axiosWrapper";
import { getTaskId } from "@CubeComponents/constants";
import { useTranslation } from "@i18n";
import Shimmer from "@Components/Queries/Shimmer";
import SlickGrid1, {
  typedColumns,
  type ColumnDefinition,
  type GridRowBase,
  type SlickgridReactInstanceX,
  type ToolbarItem,
} from "@CubeComponents/html/SlickGrid1";
import { convertDate } from "@Services/luxonHelper";
import { Formatters } from "slickgrid-react";
import { RootState } from "@Reducers/index";
import type { GetContractLinkedEntitiesResponse } from "@ServerTypes/contracts/contracts.schema";

/**
 * Row type for contract task data. The list endpoint's response is
 * `result: z.array(z.any())` (`getContractLinkedEntitiesResponse`), so there is no server row type to
 * import and only the fields this grid reads are named. SERVER SCHEMA GAP — reported.
 */
export interface ContractTaskRow extends GridRowBase {
  scheduleCounter?: string;
  title?: string;
  startDate?: number;
  endDate?: number;
  plannedStartDate?: number;
  plannedEndDate?: number;
  actualStartDate?: number;
  actualEndDate?: number;
  progress?: number;
  createdDate?: number;
  hasChild?: boolean;
  parentId?: string;
  originalTaskId?: string;
  isAlreadyAddedInThisForm?: boolean;
  isAlreadyAddedInThisContract?: boolean;
}

/**
 * Renders the Title column with slickgrid's own tree formatter.
 *
 * `Formatters.tree` cannot be handed to `ColumnDefinition["formatter"]` directly: it is typed
 * `Formatter<any>`, which takes the vendor's `Column` and a REQUIRED `grid`, while this module's
 * `formatter` slot passes its own `ColumnDefinition` (not assignable to the vendor's `Column` —
 * `excelExportOptions.valueParserCallback` returns `unknown` where the vendor's
 * `GetDataValueCallback` does not) and an OPTIONAL `grid`. The column the library would have
 * passed is therefore read back off the grid at the library's own type: `appendCellHtml` calls the
 * formatter with `this.columns[cell]` and `getColumns()` returns `this.columns`
 * (node_modules/@slickgrid-universal/common/dist/esm/core/slickGrid.js). Same wrapper as
 * SlickGrid1/GridColumnChooser.tsx.
 */
const titleTreeFormatter: NonNullable<ColumnDefinition<ContractTaskRow, "title">["formatter"]> = (
  row,
  cell,
  value,
  _columnDef,
  dataContext,
  grid,
) => {
  const column = grid?.getColumns()[cell];
  if (!grid || !column) return "";
  return Formatters.tree(row, cell, value, column, dataContext, grid);
};

/**
 * The SlickGrid1 imperative handle this grid drives, with the two row reads re-typed.
 *
 * It is an INTERSECTION with the instance type, not a hand-written subset: a `RefObject` of a
 * subset is not assignable to `Ref<SlickgridReactInstanceX<ContractTaskRow>>` (a ref is checked on
 * its `current`, and a subset is not the instance), which is what used to force an assertion at the
 * `ref=` below. The intersection is assignable, so the ref goes across as a plain assignment.
 *
 * The override comes FIRST because TypeScript resolves an intersection's call signatures in
 * declaration order: the vendor declares `slickGrid: SlickGrid` with `TData = any`, so reading the
 * wide member first would hand every row back as `any`. Written this way, `getDataItem` resolves to
 * the row type this grid actually puts in the dataset.
 *
 * `getDataItem` is nullable on purpose: SlickGrid returns `this.data[i]`, which is `undefined` for a
 * selected row index the DataView no longer resolves (a selection kept across a refresh/filter) and
 * for a group row when grouping is enabled — which this grid offers (`enableGroupingOption` below).
 * The holes are real, so callers keep their guards.
 */
type ContractTasksGridHandle = {
  slickGrid: {
    getSelectedRows: () => number[];
    getDataItem: (row: number) => ContractTaskRow | undefined;
  };
} & SlickgridReactInstanceX<ContractTaskRow>;

/** Per-caller overrides for where the grid reads from and what sits in its toolbar. */
interface ContractTasksConfigParams {
  dataSourcePayload?: unknown;
  dataSourceUrl?: string;
  idMapping?: string;
  toolbar?: ToolbarItem[] | null;
}

interface ContractTasksProps {
  contractId?: string;
  formId?: string;
  setAttachBtnState?: (state: boolean) => void;
  showAddToListOption?: boolean;
  configParams?: ContractTasksConfigParams;
  selectedTasksList?: ContractTaskRow[];
  /**
   * Only required by the add-to-list footer, which renders solely when `showAddToListOption` is set;
   * `InspectionCreation` mounts this grid without it and leaves that flag at its `false` default.
   */
  setSelectedTasksList?: React.Dispatch<React.SetStateAction<ContractTaskRow[]>>;
  /**
   * Passed by `mileStones.tsx` but not read here — selection is always enabled on this grid via
   * `enableSelectAllCheckbox`. Declared so that call site keeps type-checking.
   */
  allowCheckBoxSelection?: boolean;
}

const ContractTasks = forwardRef<ContractTasksGridHandle | null, ContractTasksProps>(
  (
    {
      contractId,
      formId,
      setAttachBtnState,
      showAddToListOption = false,
      configParams = {},
      selectedTasksList = [],
      setSelectedTasksList,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const contractDetails = useSelector((state: RootState) => state.contractDetails);
    const portalData = useSelector((state: RootState) => state.portalData);
    const localRef = useRef<ContractTasksGridHandle | null>(null);
    // A `ForwardedRef` is one of three things: an object ref, a CALLBACK ref, or null. Only an object
    // ref can back the handle this grid reads through `.current`, so the other two fall back to the
    // internal ref — same narrowing SlickGrid1 does on its own forwarded ref (SlickGrid1/index.tsx),
    // and the only arm any caller here actually passes.
    const treeGrid = ref !== null && typeof ref === "object" ? ref : localRef;
    const [addToListBtnState, setAddToListBtnState] = useState(false);
    const [dataSource, setDataSource] = useState<ContractTaskRow[]>([]);
    const [showDisclaimer, setShowDisclaimer] = useState(false);
    const [showTaskGridLoader, setShowTaskGridLoader] = useState(true);

    const isVEPLPortal = portalData.frontendUrl?.includes("vepl");

    const payload = {
      value: {
        scheduleId: contractDetails.linkedSchedule?._id,
        contractId,
        entity: "tasks",
        contractVersionId: contractDetails.versionId,
        formId,
      },
    };

    const {
      dataSourcePayload = payload,
      dataSourceUrl = contractEntitiesUrl,
      idMapping = "originalTaskId",
      toolbar = null,
    } = configParams;

    const fetchData = async () => {
      // Dynamic dataSourceUrl - endpoint not known at compile time
      const res = await axiosWrapper.post<GetContractLinkedEntitiesResponse>(dataSourceUrl, dataSourcePayload);

      if (res.data.status && res.data.result) {
        // `result` is `z.array(z.record(z.string(), z.unknown())).optional()` on
        // `getContractLinkedEntitiesResponse` — heterogeneous Mongo documents from whichever of
        // `contractsHasTasks`/`contractsHasFiles` the `entity` selects, which is why the server has
        // no element schema to parse against (census: contracts.schema.ts, `result:`). The rows are
        // handed over WHOLE, not projected: the grid's `parentIdMapping` reads `parentUniqueId`,
        // one of the extra keys no element type names. Every field `ContractTaskRow` declares is
        // optional, so nothing here is claimed to be present. SERVER SCHEMA GAP — reported.
        setDataSource(res.data.result as ContractTaskRow[]);
      }
      setShowTaskGridLoader(false);
    };

    useEffect(() => {
      fetchData();
    }, []);

    useEffect(() => {
      if (treeGrid.current) {
        treeGrid.current.dataset = dataSource;
        if (formId && isVEPLPortal) {
          // setShowDisclaimer(true);
          // const todaysTimeStamp = Date.now();
          // const sevenDayTimeStamp = 604800000;
          // const startDateRange = todaysTimeStamp - sevenDayTimeStamp;
          // const endDateRange = todaysTimeStamp + sevenDayTimeStamp;
          // const startDateFilter = {
          //   field: "startDate",
          //   operator: "greaterthanorequal",
          //   value: startDateRange,
          // };
          // const endDateFilter = {
          //   field: "endDate",
          //   operator: "lessthanorequal",
          //   value: endDateRange,
          // };
          // const filterSettings = [startDateFilter, endDateFilter];
          // treeGrid.current?.setProperties({
          //   filterSettings: { columns: filterSettings },
          // });
        }
      }
    }, [dataSource.length, portalData]);

    const selectionHandlerSlickGrid = () => {
      // Non-null on the ref: the selection handler is wired to the grid itself, so it cannot fire
      // before the grid mounts. Erased at emit — an unattached ref still throws exactly as today.
      const selectedData = treeGrid
        .current!.slickGrid.getSelectedRows()
        .map((data) => treeGrid.current!.slickGrid.getDataItem(data));
      if (selectedData.length > 0) {
        let btnState = true;

        selectedData.map((item) => {
          if (item && (item.isAlreadyAddedInThisForm || item.isAlreadyAddedInThisContract)) {
            btnState = false;
          }
        });
        setAddToListBtnState(btnState);
        if (setAttachBtnState) {
          setAttachBtnState(btnState);
        }
      } else {
        setAddToListBtnState(false);
        if (setAttachBtnState) {
          setAttachBtnState(false);
        }
      }
    };
    const plannedPercentTemplate = (
      _row: number,
      _cell: number,
      _value: number | undefined,
      _columnDef: unknown,
      dataContext: ContractTaskRow,
    ) => {
      if (!dataContext.plannedStartDate) {
        return "-";
      }
      if (new Date(dataContext.plannedStartDate).getTime() >= new Date().getTime()) {
        return "0 %";
      }
      if (!dataContext.plannedEndDate) {
        return "-";
      }
      if (new Date(dataContext.plannedEndDate).getTime() <= new Date().getTime()) {
        return "100 %";
      }
      const totalPlannedDuration = dataContext.plannedEndDate - dataContext.plannedStartDate;
      const currentDuration = Date.now() - dataContext.plannedStartDate;
      const plannedPercentage = (currentDuration / totalPlannedDuration) * 100;
      return `${plannedPercentage.toFixed(2)} %`;
    };

    const percentCompleteTemplate = (
      _row: number,
      _cell: number,
      _value: number | undefined,
      _columnDef: unknown,
      dataContext: ContractTaskRow,
    ) => {
      const milisecondsInADay = 86400000;
      const actualDuration = (Date.now() - (dataContext.actualStartDate ?? 0)) / milisecondsInADay;
      const totalDuration = ((dataContext.endDate ?? 0) - (dataContext.startDate ?? 0)) / milisecondsInADay;

      if (dataContext.actualStartDate && dataContext.actualEndDate) {
        return "100 %";
      }
      if (!dataContext.actualStartDate && !dataContext.actualEndDate) {
        return "0 %";
      }
      if (actualDuration > totalDuration) {
        return `100 %`;
      }
      if (totalDuration > 0) {
        const progress = (actualDuration / totalDuration) * 100;
        return `${progress.toFixed(2)} %`;
      }
      return "0 %";
    };

    const idFormatter = (
      _row: number,
      _cell: number,
      _value: string | undefined,
      _columnDef: unknown,
      dataContext: ContractTaskRow,
    ) => getTaskId(dataContext);

    const formatStartDate = (
      _row: number,
      _cell: number,
      _value: number | undefined,
      _columnDef: unknown,
      dataContext: ContractTaskRow,
    ) => convertDate(Number(dataContext.startDate), "dateForTooltip");

    const formatEndDate = (
      _row: number,
      _cell: number,
      _value: number | undefined,
      _columnDef: unknown,
      dataContext: ContractTaskRow,
    ) => (dataContext.endDate ? convertDate(Number(dataContext.endDate), "dateForTooltip") : "-");

    const formatPlannedStartDate = (
      _row: number,
      _cell: number,
      _value: number | undefined,
      _columnDef: unknown,
      dataContext: ContractTaskRow,
    ) => (dataContext.plannedStartDate ? convertDate(Number(dataContext.plannedStartDate), "dateForTooltip") : "-");

    const formatPlannedEndDate = (
      _row: number,
      _cell: number,
      _value: number | undefined,
      _columnDef: unknown,
      dataContext: ContractTaskRow,
    ) => (dataContext.plannedEndDate ? convertDate(Number(dataContext.plannedEndDate), "dateForTooltip") : "-");

    const formatActualStartDate = (
      _row: number,
      _cell: number,
      _value: number | undefined,
      _columnDef: unknown,
      dataContext: ContractTaskRow,
    ) => (dataContext.actualStartDate ? convertDate(Number(dataContext.actualStartDate), "dateForTooltip") : "-");

    const formatActualEndDate = (
      _row: number,
      _cell: number,
      _value: number | undefined,
      _columnDef: unknown,
      dataContext: ContractTaskRow,
    ) => (dataContext.actualEndDate ? convertDate(Number(dataContext.actualEndDate), "dateForTooltip") : "-");

    const formatCreatedDate = (
      _row: number,
      _cell: number,
      _value: number | undefined,
      _columnDef: unknown,
      dataContext: ContractTaskRow,
    ) => convertDate(Number(dataContext.createdDate), "dateForTooltip");

    // EVERY column, Title included, goes through `typedColumns()`, so each `field` keeps its
    // `field extends keyof ContractTaskRow` check. An earlier pass split Title out through the
    // brand-minting escape hatch because `Formatters.tree` cannot be assigned to the `formatter`
    // slot as-is; it does not have to be — `titleTreeFormatter` above wraps it at this module's own
    // signature, so all ten columns stay checked.
    const columnDefinitions = useMemo(
      () =>
        typedColumns<ContractTaskRow>([
          {
            field: "scheduleCounter",
            id: "scheduleCounter",
            name: t("ID"),
            minWidth: 120,
            formatter: idFormatter,
          },
          {
            field: "title",
            id: "title",
            name: t("Title"),
            filterType: "Text",
            minWidth: 150,
            formatter: titleTreeFormatter,
          },
          {
            field: "startDate",
            id: "startDate",
            name: t("Start Date"),
            filterType: "Date",
            minWidth: 120,
            formatter: formatStartDate,
            _excelValueGetter: (row) => convertDate(Number(row.startDate), "dateForTooltip"),
          },
          {
            field: "endDate",
            id: "endDate",
            name: t("End Date"),
            filterType: "Date",
            minWidth: 120,
            formatter: formatEndDate,
            _excelValueGetter: (row) => (row.endDate ? convertDate(Number(row.endDate), "dateForTooltip") : "-"),
          },
          {
            field: "plannedStartDate",
            id: "plannedStartDate",
            name: t("Planned Start Date"),
            filterType: "Date",
            minWidth: 120,
            formatter: formatPlannedStartDate,
            _excelValueGetter: (row) =>
              row.plannedStartDate ? convertDate(Number(row.plannedStartDate), "dateForTooltip") : "-",
          },
          {
            field: "plannedEndDate",
            id: "plannedEndDate",
            name: t("Planned End Date"),
            filterType: "Date",
            minWidth: 120,
            formatter: formatPlannedEndDate,
            _excelValueGetter: (row) =>
              row.plannedEndDate ? convertDate(Number(row.plannedEndDate), "dateForTooltip") : "-",
          },
          {
            field: "actualStartDate",
            id: "actualStartDate",
            name: t("Actual Start Date"),
            filterType: "Date",
            minWidth: 120,
            formatter: formatActualStartDate,
            _excelValueGetter: (row) =>
              row.actualStartDate ? convertDate(Number(row.actualStartDate), "dateForTooltip") : "-",
          },
          {
            field: "actualEndDate",
            id: "actualEndDate",
            name: t("Actual End Date"),
            filterType: "Date",
            minWidth: 120,
            formatter: formatActualEndDate,
            _excelValueGetter: (row) =>
              row.actualEndDate ? convertDate(Number(row.actualEndDate), "dateForTooltip") : "-",
          },
          {
            field: "progress",
            id: "plannedProgress",
            name: t("Planned %"),
            formatter: plannedPercentTemplate,
            filterType: "Number",
            minWidth: 120,
            _excelValueGetter: (row) => {
              if (!row.plannedStartDate) return "-";
              if (new Date(row.plannedStartDate).getTime() >= new Date().getTime()) return "0 %";
              if (!row.plannedEndDate) return "-";
              if (new Date(row.plannedEndDate).getTime() <= new Date().getTime()) return "100 %";
              const totalPlannedDuration = row.plannedEndDate - row.plannedStartDate;
              const currentDuration = Date.now() - row.plannedStartDate;
              return `${((currentDuration / totalPlannedDuration) * 100).toFixed(2)} %`;
            },
          },
          {
            field: "progress",
            id: "percentComplete",
            name: t("% Complete"),
            formatter: percentCompleteTemplate,
            filterType: "Number",
            minWidth: 120,
            _excelValueGetter: (row) => {
              const milisecondsInADay = 86400000;
              const actualDuration = (Date.now() - (row.actualStartDate ?? 0)) / milisecondsInADay;
              const totalDuration = ((row.endDate ?? 0) - (row.startDate ?? 0)) / milisecondsInADay;
              if (row.actualStartDate && row.actualEndDate) return "100 %";
              if (!row.actualStartDate && !row.actualEndDate) return "0 %";
              if (actualDuration > totalDuration) return "100 %";
              if (totalDuration > 0) return `${((actualDuration / totalDuration) * 100).toFixed(2)} %`;
              return "0 %";
            },
          },
          {
            field: "createdDate",
            id: "createdDate",
            name: t("Created On"),
            formatter: formatCreatedDate,
            filterType: "Date",
            minWidth: 120,
            _excelValueGetter: (row) => convertDate(Number(row.createdDate), "dateForTooltip"),
          },
        ]),
      [],
    );

    const expandButtonTemplate = () => (
      <div>
        <ButtonComponent
          buttonType={BUTTON_TYPES.TEXT_BUTTON}
          onClick={() => treeGrid.current?.treeDataService.toggleTreeDataCollapse(false)}
          id="expand_all"
        >
          <span className="flex items-center gap-1" data-testid="expandAllButton">
            <IconComponent name="Expand" width="1rem" height="1rem" />
            {t("Expand All")}
          </span>
        </ButtonComponent>
      </div>
    );

    const collapseButtonTemplate = () => (
      <div>
        <ButtonComponent
          buttonType={BUTTON_TYPES.TEXT_BUTTON}
          onClick={() => treeGrid.current?.treeDataService.toggleTreeDataCollapse(true)}
          id="collapse_all"
        >
          <span className="flex items-center gap-1" data-testid="collapseAllButton">
            <IconComponent name="Collapse" width="1rem" height="1rem" />
            {t("Collapse All")}
          </span>
        </ButtonComponent>
      </div>
    );

    const toolbarOptions = [
      {
        id: "ExpandAll",
        template: expandButtonTemplate,
      },
      {
        id: "CollapseAll",
        template: collapseButtonTemplate,
      },
    ];

    const getTaskGrid = () => (
      <SlickGrid1
        ref={treeGrid}
        treeGrid
        dataset={dataSource}
        idMapping={idMapping}
        hasChildMapping="hasChild"
        parentIdMapping="parentUniqueId"
        enableSelectAllCheckbox
        toolbar={toolbar ? [...toolbar, ...toolbarOptions] : toolbarOptions}
        onSelectedRowIdsChanged={selectionHandlerSlickGrid}
        initialSortColumnId="createdDate"
        enableFiltering={true}
        disableCustomSorting
        gridOptions={{
          enableSorting: true,
        }}
        columnDefinitions={columnDefinitions}
        enableFilteringOption
        enableSortingOption
        enableGroupingOption
        enableColumnChooser
        hideNoDataIllustration
      />
    );
    const getEmptyIllustration = () => <div className="m-6">{t("No Schedule have been linked to this contract")}</div>;

    const handleAddToListTask = () => {
      // Non-null on the ref and on `setSelectedTasksList`: this handler renders inside the
      // add-to-list footer, which only exists when `showAddToListOption` is set, and the callers that
      // set it also supply the setter. Both assertions are erased at emit.
      const selectedTasks = treeGrid
        .current!.slickGrid.getSelectedRows()
        .map((data) => treeGrid.current!.slickGrid.getDataItem(data))
        // A selected row index the DataView cannot resolve yields no item; the footer below reads
        // `task.originalTaskId` and `getTaskId(task)` off every entry, so the holes stop here.
        .filter((task) => task !== undefined);
      setSelectedTasksList!((prev) => [...prev, ...selectedTasks]);
    };

    const removeTaskClick = (originalTaskId: ContractTaskRow["originalTaskId"]) => {
      setSelectedTasksList!((prev) => prev.filter((task) => task.originalTaskId != originalTaskId));
    };

    const addToList = () => (
      <div className="w-full justify-between h-1/6 items-center flex">
        <div className="w-full h-full flex flex-wrap gap-3 overflow-auto">
          {selectedTasksList.map((task) => (
            <div
              key={task.originalTaskId}
              className="border-2 h-8 max-w-40 gap-2 ps-1 pe-1 border-button_light rounded flex items-center"
            >
              <span className="whitespace-nowrap overflow-hidden text-ellipsis">{getTaskId(task)}</span>
              <span>
                <GrClose size={10} className="cursor-pointer" onClick={() => removeTaskClick(task.originalTaskId)} />
              </span>
            </div>
          ))}
        </div>
        <div>
          <ButtonComponent
            onClick={handleAddToListTask}
            disabled={!addToListBtnState}
            buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
          >
            {t("Add To List")}
          </ButtonComponent>
        </div>
      </div>
    );

    const getDisclaimer = () => {
      return (
        <div className="opacity-50 p-2 bg-yellow-200 border rounded-lg flex justify-between items-center">
          <div className="text-sm">
            {t("A Preset filter is being applied , please clear filters to see all activities")}
          </div>
          <div>
            <IconComponent
              onClick={() => setShowDisclaimer(false)}
              className="cursor-pointer items-start"
              name="cross"
              width="1rem"
              height="1rem"
            />
          </div>
        </div>
      );
    };

    return (
      <div className="flex flex-col h-full gap-5">
        {formId && showDisclaimer && getDisclaimer()}
        {contractDetails.linkedSchedule?._id ? (
          showTaskGridLoader ? (
            <Shimmer />
          ) : (
            getTaskGrid()
          )
        ) : (
          getEmptyIllustration()
        )}
        {showAddToListOption && contractDetails.linkedSchedule?._id && addToList()}
      </div>
    );
  },
);
ContractTasks.displayName = "ContractTasks";
export default ContractTasks;
